<template>
  <v-container fluid class="pa-0 ma-0">
    <v-card class="step-tab-panel" flat>
      <v-row>
        <v-col class="pa-0 justify">
          <Stepper ref="stepper" :step-data="steps" />
        </v-col>
        <v-col v-if="showPreviousExtracts" cols="4" class="pt-0 pr-0 justify">
          <PreviousExtractReports :reports="reports" />
        </v-col>
      </v-row>

      <UnsavedDataModal
        ref="unsavedDataModal"
        :value="isUnsavedDataModalOpen"
        @cancel="closeUnsavedDataModal"
        @confirm="confirmUnsavedDataModal"
      />
    </v-card>
  </v-container>
</template>

<script>
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { useClusteringStore } from '@/stores/clustering';
import { useScenariosStore } from '@/stores/scenarios';
import { useExtractsStore } from '@/stores/extracts';
import extractsUtils from '@/utils/extracts-utils';
import stepperData from '@/utils/tabs/stepper-steps-enum';
import extractTypes from '@enums/extract-types';
import { flatten, get, isEqual, map, size, some, sortBy, values } from 'lodash';
import { mapActions, mapState } from 'pinia';

export default {
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeave(to, from, next);
  },

  setup() {
    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
    } = useUnsavedDataWarning();
    return {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
    };
  },

  computed: {
    ...mapState(useExtractsStore, ['extracts', 'selectedStep']),
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useClusteringStore, ['noSelectedClusterScheme']),

    showPreviousExtracts() {
      // Hide previous extracts for specific steps in JDA tab
      return this.selectedStep === 'report-jda';
    },

    steps() {
      const excludedSubSections = [];
      if (this.noSelectedClusterScheme) excludedSubSections.push('storeAllocation');

      const displayedSteps = stepperData.jda.filter(
        step => !excludedSubSections.includes(step.statusSubSection)
      );

      return map(sortBy(displayedSteps, 'order'), (tab, ix) => {
        if (isEqual(tab.statusSubSection, 'jda') && this.reports) {
          tab.isClickable = !!size(this.reports);
        }

        // Ensure order is still 1,2,3 after steps are potentially excluded
        tab.order = ix + 1;
        return tab;
      });
    },

    reports() {
      return extractsUtils.reports({
        extracts: this.extracts,
        reportType: extractTypes.STORE_EXECUTION,
      });
    },

    hasDataChanges() {
      const components = flatten(values(this.$refs.stepper.$refs));
      return some(components.map(v => v.hasDataChanges));
    },
  },

  async created() {
    this.preselectStep();
    await this.fetchScenarioClusters();
  },

  methods: {
    ...mapActions(useExtractsStore, ['setSelectedStep']),
    ...mapActions(useClusteringStore, ['fetchScenarioClusters']),

    preselectStep() {
      const isFromNotification = get(this.$route, 'params.fromNotifications', false);
      // Reset selected step as stepper is not visible initially, if coming from notifications, set to last position
      const initialStep = isFromNotification ? this.steps.length : 1;
      this.setSelectedStep(initialStep);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.step-tab-panel {
  background-color: $assortment-background !important;
}
</style>
